import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { CourseValidationErrors } from "@src/components/libraryItemDetailPages/course/types/validation";

import { usePrevious } from "@hooks/usePrevious";
import useCourseScreensAtom, {
  CourseScreen,
} from "@src/components/libraryItemDetailPages/course/hooks/useCourseScreensAtom";
import useCourseCommentThreads from "@src/components/libraryItemDetailPages/course/hooks/useCourseCommentThreads";
import useUpdateAILessons from "@src/components/libraryItemDetailPages/course/hooks/useUpdateAiLessons";
import useCourseVersionAIRequests from "@src/components/libraryItemDetailPages/course/hooks/useCourseVersionAIRequests";
import { CourseContentCourseVersionFragment } from "../build/CourseBuildContent.generated";
import {
  PromptTab,
  useLibraryItemQueryParams,
} from "../../contexts/LibraryItemQueryParamsContext";
import useContentPermissions from "../../hooks/useContentPermissions";
import { selectAtom, useAtomCallback } from "jotai/utils";
import { Atom, atom, useAtomValue } from "jotai";
import {
  courseDraftVersionSelectedAtom,
  coursePublishVersionSelectedAtom,
  courseVersionCourseIdAtom,
  courseVersionIdAtom,
  selectedScreenIdsAtom,
} from "../atoms";
import { isEqual } from "lodash";

type SetCourseValidationErrorsFn = (
  fn: (oldValidationErrors: CourseValidationErrors) => CourseValidationErrors,
) => void;

export type ScrollDirection = "up" | "down" | "none";

type Props = {
  newCourseVersion?: CourseContentCourseVersionFragment | null | undefined;
  setCourseVersion?: (
    courseVersion: CourseContentCourseVersionFragment | undefined | null,
  ) => void;
  validationErrorsAtom: Atom<CourseValidationErrors | undefined>;
  setValidationErrors?: SetCourseValidationErrorsFn;
  marketplacePath?: boolean;
  children?: JSX.Element;
};

export interface CourseBuilderContextState {
  setValidationErrors?: SetCourseValidationErrorsFn;
  activeScreenId?: string;
  activeScreenAtom: Atom<CourseScreen | undefined>;
  screensAtom: Atom<CourseScreen[]>;
  setActiveScreenId: (promptUuid: string, promptTab?: PromptTab) => void;
  selectedScreensAtom?: Atom<CourseScreen[]>;
  selectNextScreen: () => void;
  selectPrevScreen: () => void;
  draftVersionSelected: boolean;
  publishedVersionSelected: boolean;
  setPromptUuidsGeneratedByAi: React.Dispatch<
    React.SetStateAction<Set<string>>
  >;
  animationScrollDirection: ScrollDirection;
  marketplacePath: boolean;
}

export const CourseBuilderContext = React.createContext<
  CourseBuilderContextState | undefined
>(undefined);

const CourseBuilderStateProvider: FC<Props> = (props) => {
  const draftVersionSelected = useAtomValue(courseDraftVersionSelectedAtom);
  const publishedVersionSelected = useAtomValue(
    coursePublishVersionSelectedAtom,
  );
  const courseVersionId = useAtomValue(courseVersionIdAtom);
  const [promptUuidsGeneratedByAI, setPromptUuidsGeneratedByAi] = useState<
    Set<string>
  >(new Set());
  const { selectedPromptId, setPromptId } = useLibraryItemQueryParams();
  const args = useMemo(() => ({ courseVersionId }), [courseVersionId]);
  const { commentThreads } = useCourseCommentThreads(args);
  const { aiRequests } = useCourseVersionAIRequests({
    courseVersionId: courseVersionId,
  });
  const { screensAtom, screensMapAtom } = useCourseScreensAtom(
    props.validationErrorsAtom,
    promptUuidsGeneratedByAI,
    commentThreads,
    aiRequests,
  );
  const selectedScreenIds = useAtomValue(selectedScreenIdsAtom);
  const selectedScreensAtom = useMemo(
    () =>
      selectAtom(
        screensAtom,
        (screens) => {
          return screens.filter((screen) =>
            selectedScreenIds.includes(screen.id),
          );
        },
        isEqual,
      ),
    [screensAtom, selectedScreenIds],
  );
  const prevActiveScreenId = usePrevious(selectedPromptId);
  const activeScreenAtom = useMemo(
    () =>
      selectAtom(
        screensMapAtom,
        (val) => val.get(selectedPromptId || ""),
        isEqual,
      ),
    [screensMapAtom, selectedPromptId],
  );
  const courseVersionCourseId = useAtomValue(courseVersionCourseIdAtom);
  const courseBuilderContentPermissions = useContentPermissions({
    courseId: courseVersionCourseId,
  });
  const defaultPromptTab = useMemo(() => {
    return courseBuilderContentPermissions?.hasEditContentPermission
      ? PromptTab.Design
      : PromptTab.Comments;
  }, [courseBuilderContentPermissions?.hasEditContentPermission]);
  const updatePromptId = useAtomCallback(
    useCallback(
      (get) => {
        const screensMap = get(screensMapAtom);
        const screens = get(screensAtom);
        if (
          selectedPromptId &&
          !screensMap.has(selectedPromptId) &&
          screens.length > 0
        ) {
          setPromptId(screens[0].id, defaultPromptTab);
        }
      },
      [
        defaultPromptTab,
        screensAtom,
        screensMapAtom,
        selectedPromptId,
        setPromptId,
      ],
    ),
  );
  useEffect(() => {
    updatePromptId();
  });
  useUpdateAILessons({
    initialOrPollingCourseVersion: props.newCourseVersion,
  });
  const selectNextScreen = useAtomCallback(
    useCallback(
      (get, set) => {
        const screens = get(screensAtom);
        const activeScreenIndex = get(
          selectAtom(activeScreenAtom, (val) => val?.index, isEqual),
        );
        if (activeScreenIndex === undefined) return;
        if (activeScreenIndex === screens.length - 1) return;
        const nextScreen = screens[activeScreenIndex + 1];
        if (!nextScreen) return;
        setPromptId(nextScreen.id, defaultPromptTab);
        set(selectedScreenIdsAtom, [nextScreen.id]);
      },
      [activeScreenAtom, defaultPromptTab, screensAtom, setPromptId],
    ),
  );
  const selectPrevScreen = useAtomCallback(
    useCallback(
      (get, set) => {
        const screens = get(screensAtom);
        const activeScreenIndex = get(
          selectAtom(activeScreenAtom, (val) => val?.index, isEqual),
        );
        if (!activeScreenIndex) return;
        if (activeScreenIndex === 0) return;
        const prevScreen = screens[activeScreenIndex - 1];
        if (!prevScreen) return;
        setPromptId(prevScreen.id, defaultPromptTab);
        set(selectedScreenIdsAtom, [prevScreen.id]);
      },
      [activeScreenAtom, defaultPromptTab, screensAtom, setPromptId],
    ),
  );
  const animationScrollDirectionAtom: Atom<ScrollDirection> = useMemo(
    () =>
      atom((get) => {
        const screensMap = get(screensMapAtom);
        const activeScreen = get(activeScreenAtom);
        if (!selectedPromptId || !prevActiveScreenId) return "none";
        const prevSelectedScreen = screensMap.get(prevActiveScreenId);
        if (!activeScreen || !prevSelectedScreen) return "none";
        if (Math.abs(activeScreen.index - prevSelectedScreen.index) > 1)
          return "none";
        return activeScreen.index > prevSelectedScreen.index ? "down" : "up";
      }),
    [screensMapAtom, activeScreenAtom, selectedPromptId, prevActiveScreenId],
  );
  const animationScrollDirection = useAtomValue(animationScrollDirectionAtom);
  const setActiveScreenId = useAtomCallback(
    useCallback(
      (get, set, promptUuid: string, promptTab?: PromptTab) => {
        setPromptId(promptUuid, promptTab || defaultPromptTab);
        set(selectedScreenIdsAtom, [promptUuid]);
      },
      [defaultPromptTab, setPromptId],
    ),
  );
  const contextState: CourseBuilderContextState = useMemo(() => {
    return {
      marketplacePath: props.marketplacePath || false,
      draftVersionSelected: draftVersionSelected,
      activeScreenAtom,
      publishedVersionSelected: publishedVersionSelected,
      screensAtom,
      activeScreenId: selectedPromptId,
      setActiveScreenId,
      selectedScreensAtom,
      selectNextScreen,
      selectPrevScreen,
      setValidationErrors: props.setValidationErrors,
      setPromptUuidsGeneratedByAi,
      animationScrollDirection,
    };
  }, [
    props.marketplacePath,
    props.setValidationErrors,
    draftVersionSelected,
    activeScreenAtom,
    publishedVersionSelected,
    screensAtom,
    selectedPromptId,
    setActiveScreenId,
    selectedScreensAtom,
    selectNextScreen,
    selectPrevScreen,
    animationScrollDirection,
  ]);

  return (
    <CourseBuilderContext.Provider value={contextState}>
      {props.children}
    </CourseBuilderContext.Provider>
  );
};
export default CourseBuilderStateProvider;
